/**
 * Lightweight GA4 analytics wrapper.
 *
 * Loads gtag.js on first use if VITE_GA_MEASUREMENT_ID is configured.
 * All functions are safe no-ops when the ID is missing, so the site works
 * identically with or without analytics.
 */

import { useEffect, useRef } from "react";

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    GA_MEASUREMENT_ID?: string;
  }
}

function getMeasurementId(): string | undefined {
  const fromEnv = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim();
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.GA_MEASUREMENT_ID) {
    return window.GA_MEASUREMENT_ID.trim();
  }
  return undefined;
}

let loaded = false;

function ensureLoaded(): boolean {
  if (typeof window === "undefined") return false;
  const id = getMeasurementId();
  if (!id) return false;
  if (loaded) return true;

  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = function gtag(...args) {
    window.dataLayer!.push(args);
  };
  window.gtag = window.gtag || gtag;

  if (!document.querySelector(`script[data-ga-id="${id}"]`)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    s.dataset.gaId = id;
    document.head.appendChild(s);
  }

  window.gtag!("js", new Date());
  window.gtag!("config", id, { send_page_view: true });

  loaded = true;
  return true;
}

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

/** Fire a GA4 event. Safe to call with no analytics configured. */
export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  if (!ensureLoaded()) return;
  window.gtag!("event", name, params);
}

/**
 * React hook: fires an impression event the first time `ref` enters the viewport.
 * Pass a stable `name` and optional `params` (e.g. { date: "2026-05-18" }).
 */
export function useImpression<T extends Element>(
  name: string,
  params: AnalyticsParams = {},
  options: IntersectionObserverInit = { threshold: 0.4 },
) {
  const ref = useRef<T | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || fired.current) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          trackEvent(name, params);
          io.disconnect();
          break;
        }
      }
    }, options);

    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return ref;
}

export const isAnalyticsEnabled = (): boolean => Boolean(getMeasurementId());
